import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Activity, Shield, Zap, Eye } from "lucide-react";

const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
        toast.success("Welcome back, Operator");
      } else {
        await register(email, password, username);
        toast.success("System initialized. Welcome aboard.");
      }
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 border-r border-border">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-primary flex items-center justify-center">
              <Activity className="w-6 h-6 text-black" />
            </div>
            <span className="text-2xl font-heading font-bold tracking-tight">SKYNET TRADER</span>
          </div>
          <p className="text-muted-foreground text-sm">Semi-Autonomous Trading Operating System</p>
        </div>
        
        <div className="space-y-8">
          <div className="space-y-6">
            <FeatureItem 
              icon={<Shield className="w-5 h-5" />}
              title="Risk-First Architecture"
              description="Mandatory protections. Max daily loss locks. Position limits. No gambling."
            />
            <FeatureItem 
              icon={<Zap className="w-5 h-5" />}
              title="Controlled Autonomy"
              description="4 autonomy levels. From manual to full-auto with strict boundaries."
            />
            <FeatureItem 
              icon={<Eye className="w-5 h-5" />}
              title="Full Transparency"
              description="Every decision logged. Every signal explained. Complete audit trail."
            />
          </div>
        </div>

        <div className="text-xs text-muted-foreground font-mono">
          <div>VERSION 2.0.0 // PAPER TRADING MODE</div>
          <div className="text-primary mt-1">LIVE EXECUTION DISABLED</div>
        </div>
      </div>

      {/* Right panel - Auth form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          <div className="lg:hidden mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-primary flex items-center justify-center">
                <Activity className="w-6 h-6 text-black" />
              </div>
              <span className="text-2xl font-heading font-bold tracking-tight">SKYNET TRADER</span>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-heading font-bold">
              {isLogin ? "Operator Login" : "Initialize System"}
            </h2>
            <p className="text-muted-foreground mt-2 text-sm">
              {isLogin ? "Enter credentials to access trading terminal" : "Create operator account to begin"}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="username" className="text-xs uppercase tracking-wide text-muted-foreground">
                  Operator ID
                </Label>
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required={!isLogin}
                  placeholder="Enter operator ID"
                  className="bg-card border-border h-12 font-mono"
                  data-testid="register-username-input"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-wide text-muted-foreground">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="operator@example.com"
                className="bg-card border-border h-12 font-mono"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-wide text-muted-foreground">
                Access Key
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••••••"
                className="bg-card border-border h-12"
                data-testid="login-password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-primary text-black font-semibold uppercase tracking-wide hover:bg-primary/90"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? "AUTHENTICATING..." : isLogin ? "ACCESS SYSTEM" : "INITIALIZE"}
            </Button>
          </form>

          {/* Registration disabled for security */}
        </div>
      </div>
    </div>
  );
};

const FeatureItem = ({ icon, title, description }) => (
  <div className="flex gap-4">
    <div className="w-10 h-10 border border-border flex items-center justify-center text-primary flex-shrink-0">
      {icon}
    </div>
    <div>
      <h3 className="font-semibold text-sm">{title}</h3>
      <p className="text-muted-foreground text-xs mt-1">{description}</p>
    </div>
  </div>
);

export default LoginPage;
